import { Icon } from "@/components/ui/Icon";
import { cn } from "@/lib/cn";
import { DURATION, EASE, SPRING, STAGGER } from "@/lib/motion";
import type { IconGlyph } from "@/icons";
import { motion, useReducedMotion } from "motion/react";
import { FEEDBACK_VARIANTS, type FeedbackVariant } from "./variants";

type FeedbackIconProps = {
  variant: FeedbackVariant;
  /** lg = modals/overlays, md = toasts/banners */
  size?: "md" | "lg";
  /** Overrides the variant's default icon (e.g. Clock for "store closed") */
  icon?: IconGlyph;
  /** Overrides the variant's badge background (e.g. a solid brand fill) */
  tintBg?: string;
  /** Overrides the variant's icon color (e.g. white on a solid fill) */
  accentText?: string;
  className?: string;
};

export function FeedbackIcon({
  variant,
  size = "lg",
  icon,
  tintBg: tintBgOverride,
  accentText: accentTextOverride,
  className,
}: FeedbackIconProps) {
  const { Icon: DefaultIcon, accentText: variantAccentText, tintBg: variantTintBg, haloBg } =
    FEEDBACK_VARIANTS[variant];
  const Glyph = icon ?? DefaultIcon;
  const tintBg = tintBgOverride ?? variantTintBg;
  const accentText = accentTextOverride ?? variantAccentText;
  const reduceMotion = useReducedMotion();
  const isLg = size === "lg";

  return (
    <span className={cn("relative inline-flex shrink-0", isLg ? "h-16 w-16" : "h-9 w-9", className)}>
      {!reduceMotion && (
        <motion.span
          aria-hidden
          className={cn("absolute inset-0 rounded-full", haloBg)}
          initial={{ scale: 1, opacity: 0.8 }}
          animate={{ scale: isLg ? 1.55 : 1.35, opacity: 0 }}
          transition={{ duration: DURATION.emphasis * 2, delay: DURATION.moderate, ease: EASE.enter }}
        />
      )}
      <motion.span
        className={cn("relative grid h-full w-full place-items-center rounded-full", tintBg)}
        initial={reduceMotion ? false : { scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ ...SPRING.pop, delay: STAGGER.base }}
      >
        <motion.span
          className="grid place-items-center"
          initial={reduceMotion ? false : { scale: 0, rotate: -12 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ ...SPRING.snap, delay: STAGGER.base * 3 }}
        >
          <Icon as={Glyph} weight="bold" size={isLg ? "2xl" : "md"} className={accentText} />
        </motion.span>
      </motion.span>
    </span>
  );
}
